'use client';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { formatSegment } from '@utils/route-utils';
import { usePageChrome } from '@components/layout/page-chrome-context';

/** Segments that read as opaque ids (recipes, channels, organizations). */
const ID_SEGMENT_REGEX = /^[a-zA-Z0-9_-]{16,}$/;

export function useMobileNavigation(organization_id: string) {
  const t = useTranslations();
  const pathname = usePathname();

  const segments = pathname
    .split('/')
    .filter(Boolean)
    .filter(segment => segment !== organization_id);

  const isRoot = segments.length <= 1;

  const labelFor = (segment: string | undefined): string => {
    if (!segment) return '';
    if (t.has(`navigation.${segment}`)) return t(`navigation.${segment}`);
    if (ID_SEGMENT_REGEX.test(segment)) return '';
    return formatSegment(segment);
  };

  let title = labelFor(segments[segments.length - 1]);
  // Opaque id leaf (e.g. a chat channel): fall back to the parent's label.
  if (!title && segments.length > 1) {
    title = labelFor(segments[segments.length - 2]);
  }

  const parentSegments = segments.slice(0, -1);
  const parentHref = `/${[organization_id, ...parentSegments].join('/')}`;
  const parentLabel = isRoot
    ? undefined
    : labelFor(parentSegments[parentSegments.length - 1]);

  const { chrome } = usePageChrome();
  return {
    title: chrome?.title ?? title,
    isRoot,
    parentHref: chrome?.backHref ?? parentHref,
    parentLabel: chrome?.backLabel ?? parentLabel,
    variant: chrome?.mobileVariant ?? (isRoot ? 'large-title' : 'standard'),
    chrome
  };
}
